import { useToast } from '@chakra-ui/toast';
import axios, { AxiosError } from 'axios';
import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import { IUserOnboardBase } from '../models/users';
import { IVentureBase } from '../models/ventures';
import jhsfBookingsApi from '../services/apis/jhsfBookingsApi';

import {
  authenticateSessionsService,
  ISessionUser,
  ISessionVenture,
  INewSessionUserAssignment,
} from '../services/Auth/AuthenticateSessionsService';
import { refreshSessionsService } from '../services/Auth/RefreshSessionsService';
import { socket } from '../services/Socket';
import { translateError } from '../utils/errors';

// interface IRefreshingQueueItem {
//   onSuccess: (token: string) => void;
//   onFailure: (error: AxiosError) => void;
// }

type IAuthUser = ISessionUser;

interface IAuthState {
  user: IAuthUser;
  experienceLevelsTitleMap: Map<number, string>;
  sessionUserAssignment?: INewSessionUserAssignment;
  sessionUserVentureOnboard?: IUserOnboardBase;
  sessionVenture?: ISessionVenture;
  token: string;
  refreshToken: string;
}

interface ISignInCredentials {
  email: string;
  password: string;
}

interface IAuthContextData {
  user: IAuthUser;
  assignment?: INewSessionUserAssignment;
  venture?: IVentureBase;
  signIn(credentials: ISignInCredentials): Promise<void>;
  signOut: () => void;
  loading: boolean;
  updateUser: (user: IAuthUser) => void;
  selectVenture: (ventureId: string) => void;
  experienceLevelsTitleMap: Map<number, string>;
}

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IAuthState>(() => {
    const refreshToken = localStorage.getItem('@JHSF-BOOKINGS:refreshToken');
    const token = localStorage.getItem('@JHSF-BOOKINGS:token');
    const user = localStorage.getItem('@JHSF-BOOKINGS:user');
    const userAssignment = localStorage.getItem(
      '@JHSF-BOOKINGS:userAssignment',
    );
    const venture = localStorage.getItem('@JHSF-BOOKINGS:venture');

    if (refreshToken && token && user) {
      jhsfBookingsApi.defaults.headers.authorization = `Bearer ${token}`;

      const sessionUserAssignment: INewSessionUserAssignment | undefined =
        userAssignment ? JSON.parse(userAssignment) : undefined;

      const sessionVenture: ISessionVenture | undefined = venture
        ? JSON.parse(venture)
        : undefined;

      const experienceLevelsTitleMap = new Map(
        sessionVenture?.experienceLevels.map((experienceLevel) => [
          experienceLevel.level,
          experienceLevel.title,
        ]),
      );

      return {
        refreshToken,
        token,
        user: JSON.parse(user),
        sessionUserAssignment,
        sessionVenture,
        experienceLevelsTitleMap,
      };
    }

    return {} as IAuthState;
  });

  useEffect(() => {
    if (data.user) {
      socket.connect();

      socket.emit('connected-users', {
        userId: data.user.id,
        featureGroup: data.sessionUserAssignment?.featureGroup.key,
      });
    }

    return () => {
      socket.disconnect();
    };
  }, [data.sessionUserAssignment?.featureGroup.key, data.user]);

  const signIn = useCallback(
    async ({ email, password }) => {
      setLoading(true);
      try {
        const sessionData = await authenticateSessionsService({
          email,
          password,
        });

        const {
          refreshToken,
          token,
          user,
          sessionVenture,
          sessionUserAssignment,
        } = sessionData;

        localStorage.setItem('@JHSF-BOOKINGS:refreshToken', refreshToken);
        localStorage.setItem('@JHSF-BOOKINGS:token', token);
        localStorage.setItem('@JHSF-BOOKINGS:user', JSON.stringify(user));
        localStorage.setItem(
          '@JHSF-BOOKINGS:userAssignment',
          JSON.stringify(sessionUserAssignment),
        );
        if (sessionVenture) {
          localStorage.setItem(
            '@JHSF-BOOKINGS:venture',
            JSON.stringify(sessionVenture),
          );
        }

        const experienceLevelsTitleMap = new Map(
          sessionVenture?.experienceLevels.map((experienceLevel) => [
            experienceLevel.level,
            experienceLevel.title,
          ]),
        );

        jhsfBookingsApi.defaults.headers.authorization = `Bearer ${token}`;

        setData({
          refreshToken,
          token,
          user: user as IAuthUser,
          sessionVenture,
          experienceLevelsTitleMap,
          sessionUserAssignment,
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha na autenticação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao fazer o login, verifique os dados e tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('@JHSF-BOOKINGS:refreshToken');
    localStorage.removeItem('@JHSF-BOOKINGS:token');
    localStorage.removeItem('@JHSF-BOOKINGS:user');
    localStorage.removeItem('@JHSF-BOOKINGS:userAssignment');
    localStorage.removeItem('@JHSF-BOOKINGS:venture');

    setData({} as IAuthState);
  }, []);

  const updateUser = useCallback((user: IAuthUser) => {
    // TODO: CONFERIR SE POR AQUI PODE TER SIDO ALTERADO FEATUREGROUP, ETC
    // (ATUALIZAR USERASSIGNMENT)
    localStorage.setItem('@JHSF-BOOKINGS:user', JSON.stringify(user));

    setData((prev) => ({
      refreshToken: prev.refreshToken,
      token: prev.token,
      user,
      experienceLevelsTitleMap: prev.experienceLevelsTitleMap,
    }));
  }, []);

  useEffect(() => {
    // let failedRequestQueue: Array<IRefreshingQueueItem> = [];
    // let isRefreshing = false;

    jhsfBookingsApi.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // if (error.response?.data.message === 'jwt expired') {
          //   const originalConfig = error.config;
          //   const refreshToken = localStorage.getItem(
          //     '@JHSF-BOOKINGS:refreshToken',
          //   );
          //   if (!isRefreshing && refreshToken) {
          //     isRefreshing = true;
          //     try {
          //       refreshSessionsService({
          //         refreshToken,
          //         ventureId: data.venture?.id,
          //       }).then(
          //         ({ refreshToken: newRefreshToken, token: newToken }) => {
          //           localStorage.setItem(
          //             '@JHSF-BOOKINGS:refreshToken',
          //             newRefreshToken,
          //           );
          //           localStorage.setItem('@JHSF-BOOKINGS:token', newToken);
          //           jhsfBookingsApi.defaults.headers.authorization = `Bearer ${newToken}`;
          //           failedRequestQueue.forEach((request) =>
          //             request.onSuccess(newToken),
          //           );
          //           failedRequestQueue = [];
          //           setData((prevState) => ({
          //             ...prevState,
          //             token: newToken,
          //             refreshToken: newRefreshToken,
          //           }));
          //         },
          //       );
          //       // eslint-disable-next-line @typescript-eslint/no-explicit-any
          //     } catch (err: any) {
          //       failedRequestQueue.forEach((request) => request.onFailure(err));
          //       failedRequestQueue = [];
          //     } finally {
          //       isRefreshing = false;
          //     }
          //   }
          //   return new Promise((resolve, reject) => {
          //     failedRequestQueue.push({
          //       onSuccess: (newToken: string) => {
          //         originalConfig.headers.authorization = `Bearer ${newToken}`;
          //         resolve(jhsfBookingsApi(originalConfig));
          //       },
          //       onFailure: (err: AxiosError) => {
          //         reject(err);
          //       },
          //     });
          //   });
          // }

          toast({
            title: 'Sessão expirada',
            description: 'Por favor, faça login novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });

          signOut();
        }

        return Promise.reject(error);
      },
    );
  }, [signOut, toast]);

  const selectVenture = async (ventureId: string): Promise<void> => {
    const refreshToken = localStorage.getItem('@JHSF-BOOKINGS:refreshToken');

    if (refreshToken) {
      try {
        const sessionData = await refreshSessionsService({
          refreshToken,
          ventureId,
        });

        const {
          refreshToken: newRefreshToken,
          token,
          sessionVenture,
          sessionUserAssignment,
        } = sessionData;

        localStorage.setItem('@JHSF-BOOKINGS:refreshToken', newRefreshToken);
        localStorage.setItem('@JHSF-BOOKINGS:token', token);
        localStorage.setItem(
          '@JHSF-BOOKINGS:userAssignment',
          JSON.stringify(sessionUserAssignment),
        );

        if (sessionVenture) {
          localStorage.setItem(
            '@JHSF-BOOKINGS:venture',
            JSON.stringify(sessionVenture),
          );
        }

        const experienceLevelsTitleMap = new Map(
          sessionVenture?.experienceLevels.map((experienceLevel) => [
            experienceLevel.level,
            experienceLevel.title,
          ]),
        );

        jhsfBookingsApi.defaults.headers.authorization = `Bearer ${token}`;

        setData((prevState) => ({
          ...prevState,
          refreshToken: newRefreshToken,
          token,
          sessionVenture,
          experienceLevelsTitleMap,
          sessionUserAssignment,
        }));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        toast({
          title: 'Falha ao alterar plataforma',
          description: 'Por favor, faça login novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        assignment: data.sessionUserAssignment,
        venture: data.sessionVenture,
        signIn,
        signOut,
        loading,
        updateUser,
        selectVenture,
        experienceLevelsTitleMap: data.experienceLevelsTitleMap,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContextData => useContext(AuthContext);

export { AuthProvider, useAuth };
